import { StyledSelect } from "@ando/ui";
/**
 * MembersSection — intra-org collaboration on a project.
 *
 * Phase 5a (June 13, 2026). Backed by ``/projects/{id}/members`` (BE).
 * Lists every member with their role; owners can invite + flip roles
 * + remove others; viewers + editors can self-leave. The "owner" row
 * is protected at every mutation site (BE refuses promotions; FE hides
 * the action buttons on the owner row).
 *
 * Scope-cap (intra-org Phase 5a):
 *   - The invitee picker lists only members of the SAME org. Cross-
 *     tenant invites are out of scope; the BE would 404 / 403 anyway.
 *   - "Owner" role cannot be assigned via the UI; the BE rejects it.
 *     Project ownership transfer is a Phase 5b flow.
 *
 * UX:
 *   - Top: invite form (email/user picker placeholder — Phase 5.1 will
 *     wire the real org-member typeahead via existing
 *     ``listOrganizationMembers``).
 *   - List: avatar + display name (best-effort) + role chip + actions.
 *   - Actions hidden from non-write members.
 *   - Role flip uses a small dropdown; remove triggers a confirm.
 */

import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react/macro";
import { Check, Crown, Loader2, Mail, MoreHorizontal, Search, UserPlus, UserX } from "lucide-react";
import * as React from "react";

import type { Project, ProjectMember, ProjectMemberRole } from "@ando/core/api";
import {
  AlertBanner,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SurfaceSkeleton,
  cn,
  toast,
} from "@ando/ui";
import { confirm } from "@ando/ui/dialog";

import { useOrgMembers } from "@/features/settings/hooks/useOrgMembers";

import {
  useInviteProjectMember,
  useProjectMembers,
  useRemoveProjectMember,
  useUpdateProjectMemberRole,
} from "../../hooks/useProjects.js";

export interface MembersSectionProps {
  readonly project: Project;
  /** The currently-authenticated user — needed to know whether to show
   *  destructive actions (write-permission) and to recognise self-leave. */
  readonly currentUserId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function roleLabel(role: ProjectMemberRole): React.ReactNode {
  switch (role) {
    case "owner":
      return <Trans>Owner</Trans>;
    case "editor":
      return <Trans>Editor</Trans>;
    case "viewer":
      return <Trans>Viewer</Trans>;
  }
}

function roleChipClass(role: ProjectMemberRole): string {
  switch (role) {
    case "owner":
      return "bg-ando-orange-soft text-ando-orange";
    case "editor":
      return "bg-success/15 text-success";
    case "viewer":
      return "bg-ink-1/[0.05] text-ink-3";
  }
}

function shortenId(s: string): string {
  return s.length > 12 ? `${s.slice(0, 8)}…${s.slice(-4)}` : s;
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

const MemberRow: React.FC<{
  readonly member: ProjectMember;
  readonly project: Project;
  readonly currentUserId: string;
  readonly canManage: boolean;
}> = ({ member, project, currentUserId, canManage }) => {
  const { i18n } = useLingui();
  const isOwner = member.role === "owner";
  const isSelf = member.user_id === currentUserId;
  const updateRoleMutation = useUpdateProjectMemberRole(project.project_id);
  const removeMutation = useRemoveProjectMember(project.project_id);
  const [menuOpen, setMenuOpen] = React.useState(false);

  // The kebab menu shows when:
  //   1. The current user can manage AND target isn't owner (kick / role)
  //   2. OR the target IS the current user (self-leave, even for viewers)
  const showActions = (canManage && !isOwner) || (isSelf && !isOwner);

  const handleFlip = React.useCallback(
    (next: Exclude<ProjectMemberRole, "owner">) => {
      setMenuOpen(false);
      if (next === member.role) return;
      updateRoleMutation.mutate(
        { memberUserId: member.user_id, role: next },
        {
          onError: (err) => {
            toast.error(
              err instanceof Error
                ? i18n._(msg`Impossibile aggiornare il ruolo: ${err.message}`)
                : i18n._(msg`Impossibile aggiornare il ruolo`),
            );
          },
        },
      );
    },
    [updateRoleMutation, member.user_id, member.role, i18n],
  );

  const handleRemove = React.useCallback(async () => {
    setMenuOpen(false);
    const confirmed = await confirm({
      title: isSelf
        ? i18n._(msg`Uscire dal progetto?`)
        : i18n._(msg`Rimuovere il membro?`),
      description: isSelf
        ? i18n._(
            msg`Non potrai più vedere conversazioni, attività e memoria di questo progetto fino a un nuovo invito.`,
          )
        : i18n._(
            msg`L'utente perderà l'accesso al progetto immediatamente. Potrai re-invitarlo in seguito.`,
          ),
      confirmLabel: isSelf
        ? i18n._(msg`Esci dal progetto`)
        : i18n._(msg`Rimuovi`),
      destructive: true,
    });
    if (!confirmed) return;
    removeMutation.mutate(member.user_id, {
      onError: (err) => {
        toast.error(
          err instanceof Error
            ? i18n._(msg`Impossibile rimuovere il membro: ${err.message}`)
            : i18n._(msg`Impossibile rimuovere il membro`),
        );
      },
    });
  }, [removeMutation, member.user_id, isSelf, i18n]);

  return (
    <li
      className={cn(
        "flex items-center gap-3 rounded-md px-3 py-2.5",
        "border border-hairline bg-white/70",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
          "bg-ink-1/[0.05] font-medium text-[12px] text-ink-2",
        )}
      >
        {member.user_id.slice(0, 2).toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className="truncate font-medium text-[13px] text-ink-1"
            title={member.user_id}
          >
            {isSelf ? (
              <Trans>Tu</Trans>
            ) : (
              <span className="font-mono text-[11px]">
                {shortenId(member.user_id)}
              </span>
            )}
          </span>
          {isOwner ? (
            <Crown
              className="h-3 w-3 shrink-0 text-ando-orange"
              aria-label={i18n._(msg`Owner`)}
            />
          ) : null}
        </div>
        <div className="mt-0.5 text-[11px] text-ink-3">
          <Trans>Aggiunto il</Trans>{" "}
          {new Date(member.added_at).toLocaleDateString("it-IT")}
        </div>
      </div>
      <span
        className={cn(
          "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 font-medium text-[10.5px]",
          roleChipClass(member.role),
        )}
      >
        {roleLabel(member.role)}
      </span>
      {showActions ? (
        <Popover open={menuOpen} onOpenChange={setMenuOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={i18n._(msg`Azioni membro`)}
              className={cn(
                "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
                "text-ink-3 hover:bg-ink-1/[0.06] hover:text-ink-1",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
              )}
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" sideOffset={4} className="w-44 p-1">
            {canManage && !isSelf ? (
              <>
                <button
                  type="button"
                  onClick={() => handleFlip("editor")}
                  disabled={member.role === "editor" || updateRoleMutation.isPending}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-[12px]",
                    "text-ink-1 hover:bg-ink-1/[0.04]",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                  )}
                >
                  <Trans>Imposta editor</Trans>
                </button>
                <button
                  type="button"
                  onClick={() => handleFlip("viewer")}
                  disabled={member.role === "viewer" || updateRoleMutation.isPending}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-[12px]",
                    "text-ink-1 hover:bg-ink-1/[0.04]",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                  )}
                >
                  <Trans>Imposta viewer</Trans>
                </button>
                <div className="my-1 h-px bg-hairline" aria-hidden />
              </>
            ) : null}
            <button
              type="button"
              onClick={() => void handleRemove()}
              disabled={removeMutation.isPending}
              className={cn(
                "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-[12px]",
                "text-rose-600 hover:bg-rose-50",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              <UserX className="h-3 w-3" aria-hidden />
              {isSelf ? (
                <Trans>Esci dal progetto</Trans>
              ) : (
                <Trans>Rimuovi</Trans>
              )}
            </button>
          </PopoverContent>
        </Popover>
      ) : null}
    </li>
  );
};

// ---------------------------------------------------------------------------
// Invite form
// ---------------------------------------------------------------------------

/**
 * InviteForm — typeahead picker over the org-member directory, with a
 * raw-email fallback (issue #1103) for colleagues who are in the same
 * org but not yet surfaced by ``useOrgMembers`` (e.g. the directory
 * hasn't refreshed, or the invite is more convenient by exact address).
 *
 * UX: combobox-style — input filters the live org-member list, the user
 * picks a row, the BE invite mutation fires. Members already on the
 * project are filtered out so the picker only ever shows actionable
 * candidates. When nothing matches and the typed text looks like an
 * email, an "invite by email" row appears instead — the BE resolves it
 * to a user_id itself (``find_user_id_by_email_in_org``), scoped to the
 * caller's own organization, and 404s with a friendly message if no
 * Ando account matches.
 *
 * Accessibility: the popover uses ``role="listbox"`` semantics + arrow-
 * key navigation via the browser's native list focus (clicking a row
 * pulls focus back to the input on close). Empty/loading/error states
 * each render the appropriate AlertBanner / SurfaceSkeleton primitive.
 */
const EMAIL_LIKE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const InviteForm: React.FC<{
  readonly projectId: string;
  readonly existingMemberUserIds: ReadonlySet<string>;
}> = ({ projectId, existingMemberUserIds }) => {
  const { i18n } = useLingui();
  const inviteMutation = useInviteProjectMember(projectId);
  const orgMembersQuery = useOrgMembers();

  const [query, setQuery] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [role, setRole] = React.useState<"editor" | "viewer">("editor");

  // Filter pipeline: resolved-user rows only, not yet on the project,
  // matches typed query (case-insensitive, name OR email). Memo so the
  // popover doesn't churn the list on every keystroke.
  const candidates = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (orgMembersQuery.data ?? [])
      .filter((m) => m.user_id !== null && !existingMemberUserIds.has(m.user_id))
      .filter((m) => {
        if (needle.length === 0) return true;
        const name = (m.name ?? "").toLowerCase();
        const email = (m.email ?? "").toLowerCase();
        return name.includes(needle) || email.includes(needle);
      })
      .slice(0, 12);
  }, [orgMembersQuery.data, query, existingMemberUserIds]);

  const handleInvite = React.useCallback(
    (member: { readonly user_id: string | null; readonly name: string | null; readonly email: string | null }) => {
      if (!member.user_id) return;
      setOpen(false);
      inviteMutation.mutate(
        { user_id: member.user_id, role },
        {
          onSuccess: () => {
            toast.success(
              i18n._(
                msg`Invitato: ${member.name ?? member.email ?? i18n._(msg`utente`)}`,
              ),
            );
            setQuery("");
          },
          onError: (err) => {
            toast.error(
              err instanceof Error
                ? i18n._(msg`Impossibile invitare: ${err.message}`)
                : i18n._(msg`Impossibile invitare`),
            );
          },
        },
      );
    },
    [inviteMutation, role, i18n],
  );

  const handleInviteByEmail = React.useCallback(
    (email: string) => {
      setOpen(false);
      inviteMutation.mutate(
        { email, role },
        {
          onSuccess: () => {
            toast.success(i18n._(msg`Invitato: ${email}`));
            setQuery("");
          },
          onError: (err) => {
            toast.error(
              err instanceof Error
                ? i18n._(msg`Impossibile invitare: ${err.message}`)
                : i18n._(msg`Impossibile invitare`),
            );
          },
        },
      );
    },
    [inviteMutation, role, i18n],
  );

  const trimmedQuery = query.trim();
  // ``candidates`` already excludes existing project members, so an exact
  // match on an existing member's email would otherwise fall through to the
  // "invite by email" fallback below — offering a redundant invite that only
  // fails at the BE with a confusing duplicate-membership error.
  const isExistingMemberEmail = React.useMemo(() => {
    if (!EMAIL_LIKE.test(trimmedQuery)) return false;
    const needle = trimmedQuery.toLowerCase();
    return (orgMembersQuery.data ?? []).some(
      (m) =>
        m.user_id !== null &&
        existingMemberUserIds.has(m.user_id) &&
        (m.email ?? "").toLowerCase() === needle,
    );
  }, [orgMembersQuery.data, existingMemberUserIds, trimmedQuery]);
  const canInviteByEmail =
    candidates.length === 0 && EMAIL_LIKE.test(trimmedQuery) && !isExistingMemberEmail;

  return (
    <div className="flex flex-col gap-2 rounded-md border border-hairline bg-card/40 px-3 py-3">
      <div className="font-medium text-[12px] text-ink-2">
        <Trans>Invita un membro</Trans>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <div className="relative flex-1">
              <Search
                className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 text-ink-3"
                aria-hidden
              />
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                placeholder={i18n._(msg`Cerca per nome o email…`)}
                aria-label={i18n._(msg`Cerca un membro da invitare`)}
                aria-expanded={open}
                aria-controls={`invite-listbox-${projectId}`}
                role="combobox"
                className={cn(
                  "h-9 w-full rounded-md border border-hairline bg-white/85 pr-3 pl-8",
                  "text-[13px] text-ink-1 placeholder:text-ink-3",
                  "focus-visible:border-ando-orange/45 focus-visible:outline-none",
                  "focus-visible:ring-2 focus-visible:ring-ando-orange/25",
                )}
              />
            </div>
          </PopoverTrigger>
          <PopoverContent
            id={`invite-listbox-${projectId}`}
            align="start"
            sideOffset={4}
            className="w-[var(--radix-popover-trigger-width)] p-1"
            // Keep focus on the input so the user can keep typing — Radix
            // by default pulls focus into the popover on open.
            onOpenAutoFocus={(e) => e.preventDefault()}
            role="listbox"
          >
            {orgMembersQuery.isLoading ? (
              <SurfaceSkeleton variant="card-row" count={2} />
            ) : orgMembersQuery.isError ? (
              <AlertBanner tone="error">
                <Trans>Impossibile caricare i membri dell'organizzazione.</Trans>
              </AlertBanner>
            ) : candidates.length === 0 ? (
              canInviteByEmail ? (
                <ul className="flex flex-col gap-0.5">
                  <li>
                    <button
                      type="button"
                      role="option"
                      aria-selected={false}
                      onClick={() => handleInviteByEmail(trimmedQuery)}
                      disabled={inviteMutation.isPending}
                      className={cn(
                        "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left",
                        "text-ink-1 hover:bg-ink-1/[0.04]",
                        "focus-visible:bg-ink-1/[0.06] focus-visible:outline-none",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                      )}
                    >
                      <span
                        aria-hidden
                        className={cn(
                          "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
                          "bg-ando-orange/10 text-ando-orange",
                        )}
                      >
                        <Mail className="h-3.5 w-3.5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <div className="truncate font-medium text-[12.5px]">
                          <Trans>Invita {trimmedQuery}</Trans>
                        </div>
                        <div className="truncate text-[11px] text-ink-3">
                          <Trans>Via email, se ha già un account Ando nella tua organizzazione</Trans>
                        </div>
                      </span>
                    </button>
                  </li>
                </ul>
              ) : (
                <div className="px-3 py-4 text-center text-[12px] text-ink-3">
                  {isExistingMemberEmail ? (
                    <Trans>È già membro del progetto.</Trans>
                  ) : query.length === 0 ? (
                    <Trans>Tutti i membri dell'org sono già nel progetto.</Trans>
                  ) : (
                    <Trans>Nessun risultato.</Trans>
                  )}
                </div>
              )
            ) : (
              <ul className="flex flex-col gap-0.5">
                {candidates.map((m) => {
                  const display = m.name ?? m.email ?? m.user_id ?? "?";
                  const secondary = m.name ? m.email : null;
                  return (
                    <li key={m.user_id ?? m.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={false}
                        onClick={() => handleInvite(m)}
                        disabled={inviteMutation.isPending}
                        className={cn(
                          "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left",
                          "text-ink-1 hover:bg-ink-1/[0.04]",
                          "focus-visible:bg-ink-1/[0.06] focus-visible:outline-none",
                          "disabled:cursor-not-allowed disabled:opacity-50",
                        )}
                      >
                        <span
                          aria-hidden
                          className={cn(
                            "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
                            "bg-ink-1/[0.05] font-medium text-[11px] text-ink-2",
                          )}
                        >
                          {display.slice(0, 1).toUpperCase()}
                        </span>
                        <span className="min-w-0 flex-1">
                          <div className="truncate font-medium text-[12.5px]">
                            {display}
                          </div>
                          {secondary ? (
                            <div className="truncate text-[11px] text-ink-3">
                              {secondary}
                            </div>
                          ) : null}
                        </span>
                        <Check className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </PopoverContent>
        </Popover>
        <StyledSelect
          value={role}
          onChange={(e) => setRole(e.target.value as "editor" | "viewer")}
          aria-label={i18n._(msg`Ruolo`)}
          className={cn(
            "h-9 rounded-md border border-hairline bg-white/85 px-3",
            "text-[13px] text-ink-1",
            "focus-visible:border-ando-orange/45 focus-visible:outline-none",
            "focus-visible:ring-2 focus-visible:ring-ando-orange/25",
          )}
        >
          <option value="editor">{i18n._(msg`Editor`)}</option>
          <option value="viewer">{i18n._(msg`Viewer`)}</option>
        </StyledSelect>
      </div>
      <div className="flex items-center gap-2 text-[11px] text-ink-3 leading-relaxed">
        {inviteMutation.isPending ? (
          <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
        ) : (
          <UserPlus className="h-3 w-3 shrink-0" aria-hidden />
        )}
        <span>
          <Trans>
            Cerca tra i membri della tua organizzazione oppure digita
            un'email diretta. Il ruolo Owner non è assegnabile (uno per
            progetto).
          </Trans>
        </span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export const MembersSection: React.FC<MembersSectionProps> = ({
  project,
  currentUserId,
}) => {
  const { i18n } = useLingui();
  const query = useProjectMembers(project.project_id);

  // Jun 9, 2026 — Rules-of-Hooks fix. ``useMemo`` MUST run on every
  // render in the same order; the previous code placed it after the
  // ``isLoading`` / ``isError`` early-returns, so the hook count flipped
  // from 9 → 10 when the query landed, crashing the component with
  // ``Rendered more hooks than during the previous render``. The fix
  // hoists every hook to the top of the function body — derived values
  // (``me``/``canManage``/``members``) are plain expressions, not hooks,
  // and stay below the early-return guards (cheap, side-effect free).
  const members = query.data ?? [];
  const existingMemberUserIds = React.useMemo(
    // Precomputed Set so the typeahead filter is O(1) per candidate.
    () => new Set(members.map((m) => m.user_id)),
    [members],
  );

  const me = members.find((m) => m.user_id === currentUserId);
  const canManage = me?.role === "owner" || me?.role === "editor";

  if (query.isLoading) {
    return (
      <SurfaceSkeleton
        variant="card-row"
        count={3}
        ariaLabel={i18n._(msg`Caricamento membri`)}
      />
    );
  }

  if (query.isError) {
    return (
      <AlertBanner tone="error">
        <Trans>Impossibile caricare i membri. Riprova fra poco.</Trans>
      </AlertBanner>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {canManage ? (
        <InviteForm
          projectId={project.project_id}
          existingMemberUserIds={existingMemberUserIds}
        />
      ) : null}
      <ul className="flex flex-col gap-1.5">
        {members.map((m) => (
          <MemberRow
            key={m.membership_id}
            member={m}
            project={project}
            currentUserId={currentUserId}
            canManage={canManage}
          />
        ))}
      </ul>
    </div>
  );
};

MembersSection.displayName = "MembersSection";
